import { useState } from "react";
import { Link } from "wouter";
import { useEvents, useEjectRepair, useDeviceOperation, useRefreshById, type EventRow } from "../lib/api.js";
import { StatusPill } from "./StatusPill.js";

const eventTone: Record<string, "ok" | "warn" | "bad" | "muted"> = {
  CABINET_ONLINE: "ok",
  CABINET_OFFLINE: "bad",
  CABINET_STATUS: "muted",
  BATTERY_IN: "ok",
  BATTERY_BORROW_OUT: "ok",
  BATTERY_ABNORMAL_WARNING: "warn",
  BATTERY_POPUP: "warn",
  POS_INFO_STATUS: "muted",
  ADMIN_RENTAL_ORDER: "muted",
};

const faultLabel: Record<number, string> = {
  0: "ok",
  1: "virtual power",
  2: "Apple connector",
  3: "USB connector",
  4: "USB-C",
  5: "swelling",
  6: "auth failed",
  7: "cannot charge",
  8: "duplicate id",
  9: "recall",
  10: "health substandard",
  11: "overuse limit",
};

interface ParsedPayload {
  agentAccount?: string;
  source?: string;
  eventData?: {
    cabinetId?: string;
    batteryList?: Array<{
      batteryId?: string;
      slot?: number;
      vol?: number;
      faultType?: number;
      faultCause?: string;
    }>;
    signal?: string;
    rentableCount?: number;
    totalCount?: number;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

function parsePayload(p: unknown): ParsedPayload {
  if (typeof p === "string") {
    try {
      return JSON.parse(p) as ParsedPayload;
    } catch {
      return {};
    }
  }
  return (p ?? {}) as ParsedPayload;
}

function fmt(ts: number) {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function detailLine(row: EventRow, p: ParsedPayload): string | null {
  const d = p.eventData ?? {};
  const first = d.batteryList?.[0];
  switch (row.event) {
    case "BATTERY_BORROW_OUT":
    case "BATTERY_IN":
      return first?.batteryId ? `${first.batteryId} · slot ${first.slot ?? "?"}` : null;
    case "BATTERY_ABNORMAL_WARNING":
    case "BATTERY_POPUP": {
      if (!first) return null;
      const ft = typeof first.faultType === "number" ? first.faultType : -1;
      const lbl = faultLabel[ft] ?? `fault ${ft}`;
      return `${first.batteryId} · ${lbl}`;
    }
    case "CABINET_ONLINE":
    case "CABINET_OFFLINE":
    case "CABINET_STATUS": {
      const sig = d.signal ? `signal ${d.signal}` : null;
      const slots =
        d.rentableCount !== undefined && d.totalCount !== undefined
          ? `${d.rentableCount}/${d.totalCount} avail`
          : null;
      return [sig, slots].filter(Boolean).join(" · ") || null;
    }
    default:
      return null;
  }
}

function Row({ row, expanded, onToggle }: { row: EventRow; expanded: boolean; onToggle: () => void }) {
  const tone = eventTone[row.event] ?? "muted";
  const payload = parsePayload(row.payload);
  const detail = detailLine(row, payload);
  const battery = payload.eventData?.batteryList?.[0];

  const eject = useEjectRepair();
  const op = useDeviceOperation();
  const refresh = useRefreshById();

  return (
    <div className="border-b border-zinc-800/60 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-2.5 text-left transition hover:bg-zinc-900/40"
      >
        <div className="flex items-center justify-between gap-2">
          <StatusPill tone={tone}>{row.event}</StatusPill>
          <span className="shrink-0 font-mono text-[11px] text-zinc-500">
            {fmt(row.receivedAt)}
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 font-mono text-[11px] text-zinc-500">
          <span className="truncate">{row.cabinetId}</span>
          {detail && <span className="truncate text-right text-zinc-400">{detail}</span>}
        </div>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-zinc-800/60 bg-zinc-950/40 px-4 py-3">
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="stat-label">Cabinet</div>
              <Link
                href={`/cabinets/${row.cabinetId}`}
                className="mt-1 block font-mono text-zinc-200 hover:text-brand-orange"
              >
                {row.cabinetId} →
              </Link>
            </div>
            {battery?.batteryId && (
              <div>
                <div className="stat-label">Battery</div>
                <Link
                  href={`/batteries/${battery.batteryId}`}
                  className="mt-1 block font-mono text-zinc-200 hover:text-brand-orange"
                >
                  {battery.batteryId} →
                </Link>
              </div>
            )}
            <div>
              <div className="stat-label">Event time</div>
              <div className="mt-1 font-mono text-zinc-300">{row.timestamp}</div>
            </div>
            {payload.source && (
              <div>
                <div className="stat-label">Source</div>
                <div className="mt-1 text-zinc-400">{payload.source}</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {(row.event === "BATTERY_ABNORMAL_WARNING" || row.event === "BATTERY_POPUP") &&
              battery?.batteryId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    eject.mutate({ batteryId: battery.batteryId! });
                  }}
                  disabled={eject.isPending}
                  className="btn-danger"
                >
                  Eject for repair
                </button>
              )}
            {row.event === "CABINET_OFFLINE" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  refresh.mutate(row.cabinetId);
                }}
                disabled={refresh.isPending}
                className="btn-purple"
              >
                Refresh from Bajie
              </button>
            )}
            {row.event === "CABINET_ONLINE" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  op.mutate({
                    cabinetId: row.cabinetId,
                    operationType: "report",
                    slotNum: 0,
                    reason: "ops",
                  });
                }}
                disabled={op.isPending}
                className="btn-secondary"
              >
                Request status
              </button>
            )}
            <Link href={`/cabinets/${row.cabinetId}`} className="btn-secondary">
              Open cabinet →
            </Link>
            {battery?.batteryId && (
              <Link href={`/batteries/${battery.batteryId}`} className="btn-secondary">
                Battery lifecycle →
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function EventList() {
  const { data, isLoading } = useEvents(20);
  const rows = data ?? [];
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-zinc-800/60 px-4 py-3">
        <h2 className="text-sm font-semibold text-zinc-200">Live event stream</h2>
        <span className="text-xs text-zinc-500">last 20 · click to act</span>
      </div>
      {isLoading && (
        <div className="px-4 py-8 text-center text-sm text-zinc-500">Loading…</div>
      )}
      {!isLoading && rows.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-zinc-500">
          No events yet. Webhook receiver is waiting for Bajie pushes.
        </div>
      )}
      {rows.map((r) => (
        <Row
          key={r.id}
          row={r}
          expanded={expandedId === r.id}
          onToggle={() => setExpandedId((prev) => (prev === r.id ? null : r.id))}
        />
      ))}
    </div>
  );
}
